(function (global) {
  'use strict';

  var COMPONENT_NAME = 'vl-hidden-variant-picker';
  var DATA_SELECTOR = '[data-vl-hidden-variant-picker-data]';
  var OPTION_INPUTS_SELECTOR = '[data-variant-picker-option-inputs]';
  var HIDDEN_OPTION_ATTRIBUTE = 'data-vl-variant-option-hidden';
  var PREVIOUS_DISABLED_ATTRIBUTE = 'data-vl-variant-option-disabled';
  var PREVIOUS_TABINDEX_ATTRIBUTE = 'data-vl-variant-option-tabindex';
  var ARROW_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

  function toIdSet(ids) {
    return new Set((ids || []).map(function (id) { return String(id); }));
  }

  function getVisibleVariants(variants, hiddenVariantIds) {
    var hiddenIds = hiddenVariantIds instanceof Set ? hiddenVariantIds : toIdSet(hiddenVariantIds);
    return (variants || []).filter(function (variant) {
      return !hiddenIds.has(String(variant.id));
    });
  }

  function getSelectedOptions(inputContainers) {
    return (inputContainers || []).map(function (container) {
      var checked = Array.from(container.querySelectorAll('input')).find(function (input) {
        return input.checked;
      });
      return checked ? checked.value : null;
    });
  }

  function variantMatchesSelection(variant, selectedOptions) {
    return (variant.options || []).every(function (optionValue, index) {
      return selectedOptions[index] === optionValue;
    });
  }

  function findExactVariant(variants, selectedOptions) {
    return (variants || []).find(function (variant) {
      return variantMatchesSelection(variant, selectedOptions);
    }) || null;
  }

  function findFirstVisibleVariant(variants, hiddenVariantIds) {
    return getVisibleVariants(variants, hiddenVariantIds)[0] || null;
  }

  function getMatchingVariants(variants, selectedOptions, optionIndex, candidateValue) {
    return (variants || []).filter(function (variant) {
      return (variant.options || []).every(function (variantValue, index) {
        if (index === optionIndex) return variantValue === candidateValue;
        return selectedOptions[index] === variantValue;
      });
    });
  }

  function getOptionState(allVariants, visibleVariants, selectedOptions, optionIndex, candidateValue) {
    var allMatches = getMatchingVariants(
      allVariants,
      selectedOptions,
      optionIndex,
      candidateValue
    );
    var visibleMatches = getMatchingVariants(
      visibleVariants,
      selectedOptions,
      optionIndex,
      candidateValue
    );

    return {
      exists: visibleMatches.length > 0,
      available: visibleMatches.some(function (variant) { return variant.available; }),
      hiddenOnly: allMatches.length > 0 && visibleMatches.length === 0,
      missing: allMatches.length === 0
    };
  }

  function selectVariantInputs(inputContainers, variant) {
    if (!variant) return;
    (inputContainers || []).forEach(function (container, index) {
      Array.from(container.querySelectorAll('input')).forEach(function (input) {
        input.checked = input.value === variant.options[index];
      });
    });
  }

  function disableHiddenInput(input) {
    if (!input.hasAttribute(PREVIOUS_DISABLED_ATTRIBUTE)) {
      input.setAttribute(PREVIOUS_DISABLED_ATTRIBUTE, input.disabled ? 'true' : 'false');
    }
    input.disabled = true;
  }

  function restoreInputDisabledState(input) {
    if (!input.hasAttribute(PREVIOUS_DISABLED_ATTRIBUTE)) return;
    input.disabled = input.getAttribute(PREVIOUS_DISABLED_ATTRIBUTE) === 'true';
    input.removeAttribute(PREVIOUS_DISABLED_ATTRIBUTE);
  }

  var HTMLElementBase = global.HTMLElement || class {};

  class VlHiddenVariantPicker extends HTMLElementBase {
    connectedCallback() {
      if (this.initializing || this.initialized) return;
      this.initializing = true;
      this.initialize()
        .catch(function (error) {
          console.warn('[VL hidden variant picker] Unable to initialize.', error);
        })
        .finally(() => {
          this.initializing = false;
        });
    }

    disconnectedCallback() {
      this.restorePicker();
      this.initialized = false;
    }

    async initialize() {
      var dataElement = this.querySelector(DATA_SELECTOR);
      if (!dataElement) return;

      try {
        this.data = JSON.parse(dataElement.textContent);
      } catch (error) {
        console.warn('[VL hidden variant picker] Invalid component data.', error);
        return;
      }

      this.hiddenVariantIds = toIdSet(this.data.hiddenVariantIds);
      if (this.hiddenVariantIds.size === 0) return;

      await global.customElements.whenDefined('variant-picker');
      if (!this.isConnected) return;

      this.variantPicker = document.getElementById(this.dataset.variantPickerId);
      if (!this.variantPicker || !this.isSupportedSection(this.variantPicker.dataset.section)) return;

      if (!this.patchPicker()) return;
      await this.reconcileInitialState();
      this.initialized = true;
    }

    isSupportedSection(sectionId) {
      return String(sectionId || '').includes('MainSection')
        || String(sectionId || '').includes('QuickView');
    }

    patchPicker() {
      var picker = this.variantPicker;
      if (picker.vlHiddenVariantController && picker.vlHiddenVariantController !== this) return false;

      this.originalSetVariant = picker.setVariant;
      this.originalSetOptionsAvailable = picker.setOptionsAvailable;
      picker.vlHiddenVariantController = this;
      picker.setVariant = this.setVariant.bind(this);
      picker.setOptionsAvailable = this.setOptionsAvailable.bind(this);
      this.boundHandleKeydown = this.handleKeydown.bind(this);
      document.addEventListener('keydown', this.boundHandleKeydown, true);
      return true;
    }

    restorePicker() {
      var picker = this.variantPicker;
      if (!picker || picker.vlHiddenVariantController !== this) return;
      this.restoreOptionStates();
      if (this.originalSetVariant) picker.setVariant = this.originalSetVariant;
      if (this.originalSetOptionsAvailable) picker.setOptionsAvailable = this.originalSetOptionsAvailable;
      if (this.boundHandleKeydown) document.removeEventListener('keydown', this.boundHandleKeydown, true);
      delete picker.vlHiddenVariantController;

      if (typeof picker.setOptionsAvailable === 'function') picker.setOptionsAvailable();
      if (typeof picker.updateLabels === 'function') picker.updateLabels();
    }

    get inputContainers() {
      return Array.from(this.variantPicker.querySelectorAll(OPTION_INPUTS_SELECTOR));
    }

    get visibleVariants() {
      return getVisibleVariants(this.variantPicker.variants, this.hiddenVariantIds);
    }

    setVariant() {
      var picker = this.variantPicker;
      var inputContainers = this.inputContainers;
      var selectedOptions = getSelectedOptions(inputContainers);
      var variant = findExactVariant(this.visibleVariants, selectedOptions);

      if (!variant) {
        variant = findFirstVisibleVariant(picker.variants, this.hiddenVariantIds);
        selectVariantInputs(inputContainers, variant);
      }

      picker.variant = variant;
      if (!variant && typeof picker.setUnavailable === 'function') {
        global.customElements.whenDefined('product-form').then(function () {
          picker.setUnavailable();
        });
      }
    }

    async getLabels(input) {
      var labels = Array.from(this.variantPicker.querySelectorAll('label')).filter(function (label) {
        return label.getAttribute('for') === input.id;
      });

      if (global.customElements.get('float-element')) {
        Array.from(this.variantPicker.querySelectorAll('float-element')).forEach(function (dropdown) {
          if (!dropdown.element || !dropdown.element.querySelectorAll) return;
          labels.push.apply(labels, Array.from(dropdown.element.querySelectorAll('label')).filter(function (label) {
            return label.getAttribute('for') === input.id;
          }));
        });
      }

      return Array.from(new Set(labels));
    }

    applyOptionState(input, labels, state) {
      var shouldHide = !state.exists;
      var isOutOfStock = state.exists && !state.available;

      if (shouldHide) {
        disableHiddenInput(input);
      } else {
        restoreInputDisabledState(input);
      }
      input.toggleAttribute(HIDDEN_OPTION_ATTRIBUTE, shouldHide);

      labels.forEach(function (label) {
        if (shouldHide && !label.hasAttribute(PREVIOUS_TABINDEX_ATTRIBUTE)) {
          label.setAttribute(
            PREVIOUS_TABINDEX_ATTRIBUTE,
            label.hasAttribute('tabindex') ? label.getAttribute('tabindex') : 'none'
          );
        }
        label.toggleAttribute(HIDDEN_OPTION_ATTRIBUTE, shouldHide);
        label.toggleAttribute('no-available', shouldHide || isOutOfStock);
        label.toggleAttribute('out-of-stock', isOutOfStock);
        label.toggleAttribute('no-exist', state.missing);
        label.setAttribute('aria-disabled', shouldHide || isOutOfStock ? 'true' : 'false');
        if (shouldHide) {
          label.setAttribute('tabindex', '-1');
        } else if (label.hasAttribute(PREVIOUS_TABINDEX_ATTRIBUTE)) {
          var previousTabindex = label.getAttribute(PREVIOUS_TABINDEX_ATTRIBUTE);
          if (previousTabindex === 'none') label.removeAttribute('tabindex');
          else label.setAttribute('tabindex', previousTabindex);
          label.removeAttribute(PREVIOUS_TABINDEX_ATTRIBUTE);
        }
      });
    }

    restoreOptionStates() {
      if (!this.variantPicker) return;

      Array.from(this.variantPicker.querySelectorAll('input[' + HIDDEN_OPTION_ATTRIBUTE + '], input[' + PREVIOUS_DISABLED_ATTRIBUTE + ']'))
        .forEach(function (input) {
          restoreInputDisabledState(input);
          input.removeAttribute(HIDDEN_OPTION_ATTRIBUTE);
        });

      var labelSelector = 'label[' + HIDDEN_OPTION_ATTRIBUTE + '], label[' + PREVIOUS_TABINDEX_ATTRIBUTE + ']';
      var labels = Array.from(this.variantPicker.querySelectorAll(labelSelector));

      Array.from(this.variantPicker.querySelectorAll('float-element')).forEach(function (dropdown) {
        if (!dropdown.element || !dropdown.element.querySelectorAll) return;
        labels.push.apply(labels, Array.from(dropdown.element.querySelectorAll(labelSelector)));
      });

      Array.from(new Set(labels)).forEach(function (label) {
        label.removeAttribute(HIDDEN_OPTION_ATTRIBUTE);
        if (label.hasAttribute(PREVIOUS_TABINDEX_ATTRIBUTE)) {
          var previousTabindex = label.getAttribute(PREVIOUS_TABINDEX_ATTRIBUTE);
          if (previousTabindex === 'none') label.removeAttribute('tabindex');
          else label.setAttribute('tabindex', previousTabindex);
          label.removeAttribute(PREVIOUS_TABINDEX_ATTRIBUTE);
        }
      });
    }

    handleKeydown(event) {
      if (!ARROW_KEYS.includes(event.key) && event.key !== 'Tab') return;

      var currentLabel = event.target.closest && event.target.closest('label[for]');
      if (!currentLabel) return;

      var inputId = currentLabel.getAttribute('for');
      var belongsToPicker = Array.from(this.variantPicker.querySelectorAll('input')).some(function (input) {
        return input.id === inputId;
      });
      if (!belongsToPicker) return;

      var labelContainer = currentLabel.closest('[data-variant-picker-option-labels], .variant-picker-dropdown__body');
      if (!labelContainer) return;

      var visibleLabels = Array.from(labelContainer.querySelectorAll('label[for]')).filter(function (label) {
        return !label.hasAttribute(HIDDEN_OPTION_ATTRIBUTE);
      });
      var currentIndex = visibleLabels.indexOf(currentLabel);
      if (currentIndex < 0) return;

      event.stopImmediatePropagation();
      var isPrevious = event.key === 'ArrowLeft' || event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey);
      var isNext = event.key === 'ArrowRight' || event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey);
      var targetLabel = isPrevious
        ? visibleLabels[currentIndex - 1] || (event.key === 'Tab' ? null : visibleLabels[visibleLabels.length - 1])
        : isNext
          ? visibleLabels[currentIndex + 1] || (event.key === 'Tab' ? null : visibleLabels[0])
          : null;

      if (targetLabel) {
        event.preventDefault();
        currentLabel.removeAttribute('tabindex');
        targetLabel.setAttribute('tabindex', '0');
        targetLabel.focus();
      } else if (event.key !== 'Tab') {
        event.preventDefault();
      }
    }

    async setOptionsAvailable() {
      var picker = this.variantPicker;
      var revision = (picker.vlHiddenOptionsRevision || 0) + 1;
      picker.vlHiddenOptionsRevision = revision;

      var selectedOptions = getSelectedOptions(this.inputContainers);
      var allVariants = picker.variants || [];
      var visibleVariants = this.visibleVariants;

      for (var optionIndex = 0; optionIndex < this.inputContainers.length; optionIndex += 1) {
        var inputs = Array.from(this.inputContainers[optionIndex].querySelectorAll('input'));
        for (var inputIndex = 0; inputIndex < inputs.length; inputIndex += 1) {
          var input = inputs[inputIndex];
          var state = getOptionState(
            allVariants,
            visibleVariants,
            selectedOptions,
            optionIndex,
            input.value
          );
          var labels = await this.getLabels(input);
          if (picker.vlHiddenOptionsRevision !== revision) return;
          this.applyOptionState(input, labels, state);
        }
      }
    }

    async reconcileInitialState() {
      var picker = this.variantPicker;
      var previousVariantId = picker.variant ? String(picker.variant.id) : '';

      this.setVariant();
      await this.setOptionsAvailable();
      if (typeof picker.updateLabels === 'function') picker.updateLabels();

      var nextVariantId = picker.variant ? String(picker.variant.id) : '';
      if (!picker.variant || previousVariantId === nextVariantId) return;

      if (typeof picker.updateStock === 'function') picker.updateStock();
      if (typeof picker.updatePickupAvailability === 'function') picker.updatePickupAvailability();
      if (typeof picker.updateURL === 'function') picker.updateURL();
      if (typeof picker.updateVariantInput === 'function') picker.updateVariantInput();
      if (typeof picker.updateSection === 'function') picker.updateSection();
      if (!picker.hasAttribute('data-product-media-filtering-option') && typeof picker.setFirstMedia === 'function') {
        picker.setFirstMedia(false);
      }
      if (typeof picker.emit === 'function') {
        picker.emit('change', {
          variant: picker.variant,
          option: { name: null, value: null, source: COMPONENT_NAME }
        });
      }
    }
  }

  global.VLHiddenVariantPicker = {
    toIdSet: toIdSet,
    getVisibleVariants: getVisibleVariants,
    getSelectedOptions: getSelectedOptions,
    findExactVariant: findExactVariant,
    findFirstVisibleVariant: findFirstVisibleVariant,
    getMatchingVariants: getMatchingVariants,
    getOptionState: getOptionState,
    selectVariantInputs: selectVariantInputs,
    disableHiddenInput: disableHiddenInput,
    restoreInputDisabledState: restoreInputDisabledState
  };

  if (global.customElements && !global.customElements.get(COMPONENT_NAME)) {
    global.customElements.define(COMPONENT_NAME, VlHiddenVariantPicker);
  }
})(typeof window !== 'undefined' ? window : globalThis);
